import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ChevronDown, BookOpen, Lock, Sparkles } from "lucide-react";
import { publishingApi, paymentsApi } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { PublishingNav, PublishingFooter } from "@/components/publishing/PublishingShared";
import { BusinessInquiryModal } from "@/components/publishing/BusinessInquiryModal";

// Expandable Detail Section
const ExpandableSection = ({ title, children, defaultOpen = false, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/10 py-6">
      <button
        onClick={() => setOpen(!open)}
        data-testid={testId}
        className="w-full flex items-center justify-between text-left group"
      >
        <span className="text-white font-heading text-lg md:text-xl tracking-tight uppercase group-hover:text-jcrown-gold transition-colors">
          {title}
        </span>
        <ChevronDown 
          className={`w-5 h-5 text-white/50 group-hover:text-jcrown-gold transition-all duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div className={`grid transition-all duration-500 ${open ? 'grid-rows-[1fr] mt-4 opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <div className="text-white/60 font-body text-sm md:text-base leading-relaxed pr-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// More From Johnny Crown
const RelatedBooks = ({ currentSlug, allBooks }) => {
  const others = allBooks.filter(b => b.slug !== currentSlug).slice(0, 3);
  const [aspects, setAspects] = useState({});
  return (
    <section data-testid="related-books" className="relative bg-secondary py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <p className="text-jcrown-gold/60 text-[10px] tracking-[0.4em] uppercase font-body mb-4">
          More From Johnny Crown
        </p>
        <h2 className="font-heading text-white text-3xl md:text-5xl tracking-tighter uppercase mb-12">
          Continue Exploring
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {others.map(book => (
            <Link 
              key={book.id}
              to={`/books/${book.slug}`}
              data-testid={`related-${book.slug}`}
              className="group"
            >
              <div 
                className="relative overflow-hidden bg-black/40 mb-4 flex items-center justify-center"
                style={{ aspectRatio: aspects[book.id] || '2 / 3' }}
              >
                <img 
                  src={book.cover_image}
                  alt={book.title_display}
                  loading="lazy"
                  onLoad={(e) => setAspects(a => ({ ...a, [book.id]: `${e.target.naturalWidth} / ${e.target.naturalHeight}` }))}
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-1">{book.category}</p>
              <h3 className="font-heading text-white text-xl tracking-tight uppercase group-hover:text-jcrown-gold transition-colors">
                {book.title_display}
              </h3>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function BookDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [book, setBook] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [allBooks, setAllBooks] = useState([]);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [aspect, setAspect] = useState(null);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setAspect(null);
    publishingApi.getBook(slug)
      .then(data => {
        setBook(data.book);
        setPricing(data.pricing);
      })
      .catch(() => setNotFound(true));
    
    publishingApi.listBooks().then(data => setAllBooks(data.books)).catch(() => {});
  }, [slug]);

  const buy = async (lookup_key) => {
    if (!user) {
      navigate(`/register?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    setPurchasing(lookup_key);
    try {
      const { checkout_url } = await paymentsApi.checkout(lookup_key);
      window.location.href = checkout_url;
    } catch (e) {
      setPurchasing(null);
      alert("Checkout failed. Please try again.");
    }
  };

  if (notFound) {
    return (
      <div className="bg-primary min-h-screen flex items-center justify-center text-center px-6">
        <div>
          <h1 className="font-heading text-white text-5xl uppercase mb-4">Not Found</h1>
          <p className="text-white/50 font-body mb-8">This book isn't in the library yet.</p>
          <Link to="/books" className="text-jcrown-gold underline">← Back to Library</Link>
        </div>
      </div>
    );
  }

  if (!book || !pricing) {
    return (
      <div className="bg-primary min-h-screen flex items-center justify-center">
        <div className="text-white/30 font-body text-sm tracking-widest uppercase">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-primary min-h-screen">
      <div className="noise-overlay" />
      <PublishingNav />
      
      {/* Hero */}
      <section 
        data-testid="book-detail-hero"
        className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden"
      >
        {/* Ambient accent glow using book's color */}
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div 
            className="absolute top-1/4 -left-40 w-[600px] h-[600px] rounded-full blur-3xl"
            style={{ backgroundColor: `${book.accent_color}20` }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <Link 
            to="/books"
            data-testid="back-to-library"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors font-body text-sm mb-12"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to the Library
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Cover */}
            <div className="lg:col-span-6">
              <div 
                data-testid="book-cover"
                className="relative overflow-hidden bg-black/40 max-w-md mx-auto lg:mx-0 flex items-center justify-center"
                style={{ 
                  aspectRatio: aspect || '2 / 3',
                  boxShadow: `0 40px 80px -20px ${book.accent_color}30, 0 0 0 1px rgba(255,255,255,0.05)`
                }}
              >
                <img 
                  src={book.cover_image}
                  alt={`${book.title_display} cover`}
                  onLoad={(e) => setAspect(`${e.target.naturalWidth} / ${e.target.naturalHeight}`)}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Info */}
            <div className="lg:col-span-6">
              <p 
                className="text-[10px] tracking-[0.4em] uppercase font-body mb-4"
                style={{ color: `${book.accent_color}` }}
              >
                {book.category}
              </p>
              <h1 className="font-heading text-white text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tighter uppercase mb-6">
                {book.title_display}
              </h1>
              
              <div className="space-y-1 mb-8">
                <p className="text-white/70 font-body text-base">{book.author_credit}</p>
                {book.illustrator && (
                  <p className="text-white/50 font-body text-sm">Illustrated by {book.illustrator}</p>
                )}
              </div>

              <p className="text-white/60 font-body text-lg leading-relaxed mb-8">
                {book.short_description}
              </p>

              {/* Meta */}
              <div className="grid grid-cols-2 gap-6 pb-8 mb-8 border-b border-white/10">
                <div>
                  <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase font-body mb-2">Category</p>
                  <p className="text-white font-body text-sm">{book.category}</p>
                </div>
                <div>
                  <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase font-body mb-2">Recommended For</p>
                  <p className="text-white font-body text-sm">{book.age_range}</p>
                </div>
              </div>

              {/* Price + Actions */}
              <div className="space-y-4">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body">Digital Edition</span>
                </div>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="font-heading text-white text-5xl tracking-tight">${book.digital_price}</span>
                  <span className="text-white/40 font-body text-sm">USD</span>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link 
                    to={`/read/${book.slug}`}
                    data-testid="preview-btn"
                    className="group inline-flex items-center justify-center gap-2 border border-white/20 text-white px-6 py-4 font-body text-xs tracking-widest uppercase hover:border-white hover:bg-white/5 transition-all active:scale-95"
                  >
                    <BookOpen className="w-4 h-4" />
                    Preview Book
                  </Link>
                  <button 
                    onClick={() => buy(book.stripe_lookup_key || `individual_${book.slug}`)}
                    disabled={purchasing !== null}
                    data-testid="unlock-btn"
                    className="group inline-flex items-center justify-center gap-2 bg-jcrown-gold text-jcrown-dark px-6 py-4 font-body text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all active:scale-95 disabled:opacity-50"
                  >
                    <Lock className="w-4 h-4" />
                    {purchasing === (book.stripe_lookup_key || `individual_${book.slug}`) ? "Redirecting..." : "Unlock Digital Edition"}
                  </button>
                </div>

                <button 
                  onClick={() => buy("complete_collection")}
                  disabled={purchasing !== null}
                  data-testid="add-collection-btn"
                  className="group inline-flex items-center gap-2 text-jcrown-gold/80 hover:text-jcrown-gold font-body text-xs tracking-widest uppercase transition-colors mt-2 disabled:opacity-50"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {purchasing === "complete_collection" ? "Redirecting..." : `Or add the complete collection — $${pricing.complete_collection}`}
                  <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>

                <p className="text-white/30 font-body text-xs mt-6 tracking-widest uppercase">
                  Secure checkout by Stripe • Test mode
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expandable Details */}
      <section data-testid="book-details" className="relative bg-secondary py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6">
          <ExpandableSection title="About the Book" defaultOpen={true} testId="expand-about-book">
            <p>{book.synopsis}</p>
          </ExpandableSection>

          <ExpandableSection title="About the Creator" testId="expand-about-creator">
            <p>
              Johnny Crown is a multidisciplinary creator, recording artist, entrepreneur and storyteller whose work spans music, entertainment, fashion, original characters and publishing. Through Goodman Entertainment, Crown develops original worlds and intellectual property designed to entertain, inspire and connect with new generations.
            </p>
          </ExpandableSection>

          <ExpandableSection title="Book Details" testId="expand-details">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-1">Format</p>
                <p>Digital Edition</p>
              </div>
              <div>
                <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-1">Category</p>
                <p>{book.category}</p>
              </div>
              <div>
                <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-1">Reader</p>
                <p>{book.age_range}</p>
              </div>
              <div>
                <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-1">Publisher</p>
                <p>Goodman Publishing</p>
              </div>
              <div>
                <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-1">Preview Pages</p>
                <p>{book.preview_page_count} pages free</p>
              </div>
              <div>
                <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-1">Price</p>
                <p>${book.digital_price} USD</p>
              </div>
            </div>
          </ExpandableSection>

          <ExpandableSection title="More From Johnny Crown" testId="expand-more">
            <p>
              Explore the full Johnny Crown Library — six original books spanning fantasy, superheroes, culture, adventure and imagination.
            </p>
            <Link to="/books" className="inline-flex items-center gap-2 text-jcrown-gold hover:text-white transition-colors font-body text-sm mt-4 tracking-widest uppercase">
              View All Books <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </ExpandableSection>
        </div>
      </section>

      {allBooks.length > 0 && <RelatedBooks currentSlug={slug} allBooks={allBooks} />}
      <PublishingFooter onBusinessInquiryClick={() => setInquiryOpen(true)} />
      <BusinessInquiryModal isOpen={inquiryOpen} onClose={() => setInquiryOpen(false)} />
    </div>
  );
}
